import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, ArrowLeft, Send, AlertTriangle, Calendar, Edit3, ArrowUp, Paperclip, FileText, Save } from 'lucide-react';
import { FeedbackQuestion, PipInfo } from '../types/feedback';
import { useApiClient } from '../hooks/useApiClient';
import { useUserProfile } from '../hooks/useUserProfile';
import { useToast } from '../hooks/useToast';
import { Toast, ConfirmDialog } from '../components/Toast';
import { CorePrioritiesView } from '../components/CorePrioritiesView';
import FileUpload from '../components/FileUpload';
import { RichTextEditor } from '../components/common/RichTextEditor';
import { isRichTextEmpty, renderRichText } from '../utils/richText';
import { formatDateInIST } from '../utils/mappers';

interface LocationState {
  requestName?: string;
  questions: FeedbackQuestion[];
}

export function NewFeedbackRequest() {
  const navigate = useNavigate();
  const location = useLocation();
  const { apiCall } = useApiClient();
  const { userProfile, loading: profileLoading, fetchUserProfile } = useUserProfile();
  const { toasts, showToast, hideToast, success: showSuccess, error: showError } = useToast();
  
  const state = location.state as LocationState;
  
  // Auto-generate a default connect name based on current quarter/year
  const currentQuarter = `Q${Math.ceil((new Date().getMonth() + 1) / 3)}`;
  const currentYear = new Date().getFullYear();
  const defaultName = `${currentQuarter} ${currentYear} Connect`;
  
  const [requestName, setRequestName] = useState(state?.requestName || defaultName);
  const [isEditingName, setIsEditingName] = useState(false);
  const [questions, setQuestions] = useState<FeedbackQuestion[]>(state?.questions || []);
  const [answers, setAnswers] = useState<{ [key: number]: string }>({});
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [questionsLoading, setQuestionsLoading] = useState(!state?.questions);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [activePip, setActivePip] = useState<PipInfo | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{ name?: boolean; questions?: Set<number> }>({});
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [submitConfirmMessage, setSubmitConfirmMessage] = useState('');

  // Back-to-top scroll listener
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Progress calculation
  const answeredCount = questions.filter(q => !isRichTextEmpty(answers[q.id] || '')).length;
  const progressPercent = questions.length > 0 ? (answeredCount / questions.length) * 100 : 0;

  // Fetch user profile on mount
  useEffect(() => {
    if (!userProfile) {
      fetchUserProfile();
    }
  }, [userProfile, fetchUserProfile]);

  useEffect(() => {
    // If no state was passed, fetch questions
    if (!state?.questions && userProfile) {
      fetchQuestions();
    }
  }, [userProfile]);

  useEffect(() => {
    if (userProfile?.employeeId) {
      fetchActivePipStatus(userProfile.employeeId);
    }
  }, [userProfile?.employeeId]);

  const fetchActivePipStatus = async (employeeId: string) => {
    try {
      const response = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL}/api/Pip/employee/${encodeURIComponent(employeeId)}/active`
      );

      if (!response.ok) {
        setActivePip(null);
        return;
      }

      const result = await response.json();
      if (result.data && result.data.isOnPip) {
        setActivePip({
          id: result.data.id,
          feedbackRequestId: result.data.feedbackRequestId,
          isOnPip: result.data.isOnPip,
          pipStartDate: result.data.pipStartDate,
          pipEndDate: result.data.pipEndDate,
          pipReason: result.data.pipReason,
          setByUserId: result.data.setByUserId,
          setByUserName: result.data.setByUserName,
          createdAt: result.data.createdAt,
          updatedAt: result.data.updatedAt
        });
      } else {
        setActivePip(null);
      }
    } catch (error) {
      console.error('Error fetching active PIP status:', error);
      setActivePip(null);
    }
  };

  const fetchQuestions = async () => {
    if (!userProfile) return;
    
    setQuestionsLoading(true);
    try {
      const endpoint = userProfile?.manager?.employeeId
        ? `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackQuestions/for-employee/${encodeURIComponent(userProfile.manager.employeeId)}`
        : `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackQuestions`;
      
      const response = await apiCall(endpoint);
      if (response.ok) {
        const result = await response.json();
        if (result.data) {
          const sortedQuestions = result.data
            .sort((a: any, b: any) => a.displayOrder - b.displayOrder)
            .map((q: any) => ({
              id: q.id,
              question: q.question,
              placeholder: q.placeholder || ''
            }));
          setQuestions(sortedQuestions);
        }
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setQuestionsLoading(false);
    }
  };

  const handleAnswerChange = (questionId: number, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    // Clear validation error for this question when user types
    if (validationErrors.questions?.has(questionId)) {
      setValidationErrors(prev => {
        const newQuestions = new Set(prev.questions);
        newQuestions.delete(questionId);
        return { ...prev, questions: newQuestions.size > 0 ? newQuestions : undefined };
      });
    }
  };

  const uploadFiles = async (requestId: number, files: File[]) => {
    console.log(`Uploading ${files.length} files for request ID:`, requestId);
    try {
      for (const file of files) {
        console.log(`Uploading file: ${file.name} (${file.size} bytes)`);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('uploadedBy', userProfile?.email || 'unknown');

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/Attachments/upload/${requestId}`,
          {
            method: 'POST',
            body: formData
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`Failed to upload file: ${file.name}`, response.status, errorText);
          // Continue with other files even if one fails
        } else {
          const result = await response.json();
          console.log(`Successfully uploaded file: ${file.name}`, result);
        }
      }
      console.log('File upload completed');
    } catch (error) {
      console.error('Error uploading files:', error);
      // Don't throw - we don't want to fail the entire submission if file upload fails
    }
  };

  const handleSaveAsDraft = async () => {
    if (!userProfile) {
      alert('User profile not loaded. Please try logging in again.');
      navigate('/dashboard');
      return;
    }

    if (!userProfile.manager?.employeeId) {
      alert('Manager information is missing. Please contact your administrator.');
      return;
    }

    setIsSavingDraft(true);

    try {
      // Create the feedback request as draft
      const createRequestDto = {
        employeeId: userProfile.employeeId,
        employeeName: userProfile.displayName,
        employeeEmail: userProfile.email,
        managerEmployeeId: userProfile.manager?.employeeId || '',
        managerName: userProfile.manager?.displayName || '',
        managerEmail: userProfile.manager?.email || '',
        connectName: requestName || null,
        year: new Date().getFullYear(),
        status: 'draft'
      };

      const response = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackRequests`,
        {
          method: 'POST',
          body: JSON.stringify(createRequestDto)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save draft');
      }

      const result = await response.json();
      const requestId = result.data?.id;

      if (!requestId) {
        throw new Error('No request ID returned from server');
      }

      // Submit answers if any are filled
      const filledAnswers = questions.filter(q => answers[q.id]?.trim());
      if (filledAnswers.length > 0) {
        const submitAnswersDto = {
          userId: userProfile.employeeId,
          userName: userProfile.displayName,
          isDraft: true,
          answers: questions.map(q => ({
            questionId: q.id,
            answer: answers[q.id] || ''
          }))
        };

        const answersResponse = await apiCall(
          `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackRequests/${requestId}/answers`,
          {
            method: 'POST',
            body: JSON.stringify(submitAnswersDto)
          }
        );

        if (!answersResponse.ok) {
          throw new Error('Failed to save draft answers');
        }
      }

      // Upload files if any are selected
      if (selectedFiles.length > 0) {
        await uploadFiles(requestId, selectedFiles);
      }

      showSuccess('Connect saved as draft successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error saving draft:', error);
      showError(error instanceof Error ? error.message : 'Failed to save draft');
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleDiscard = () => {
    setShowDiscardConfirm(true);
  };

  const confirmDiscard = () => {
    setShowDiscardConfirm(false);
    navigate('/dashboard');
  };

  const validateForm = (): boolean => {
    const errors: { name?: boolean; questions?: Set<number> } = {};
    let hasErrors = false;

    // Validate connect name
    if (!requestName.trim()) {
      errors.name = true;
      hasErrors = true;
    }

    // Validate at least one question is answered
    const unanswered = new Set<number>();
    questions.forEach(q => {
      const answer = answers[q.id] || '';
      if (isRichTextEmpty(answer)) {
        unanswered.add(q.id);
      }
    });

    const answeredQuestionCount = questions.length - unanswered.size;
    if (answeredQuestionCount === 0) {
      errors.questions = unanswered;
      hasErrors = true;
    }

    setValidationErrors(errors);

    if (hasErrors) {
      // Scroll to first error
      if (errors.name) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (errors.questions && errors.questions.size > 0) {
        const firstUnanswered = questions.find(q => unanswered.has(q.id));
        if (firstUnanswered) {
          const el = document.getElementById(`question-${firstUnanswered.id}`);
          el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }

      if (errors.name && answeredQuestionCount === 0) {
        showError('Please enter a connect name and answer at least one question before submitting.');
      } else if (errors.name) {
        showError('Please enter a connect name before submitting.');
      } else {
        showError('Please answer at least one question before submitting.');
      }
    }

    return !hasErrors;
  };

  const handleSubmitClick = () => {
    if (!validateForm()) return;

    const unansweredCount = questions.filter(q => isRichTextEmpty(answers[q.id] || '')).length;
    if (unansweredCount > 0) {
      setSubmitConfirmMessage(
        `${unansweredCount} question${unansweredCount > 1 ? 's are' : ' is'} unanswered. Do you still want to submit this connect?`
      );
    } else {
      setSubmitConfirmMessage('Are you sure you want to submit this connect? Once submitted, it will be sent to your manager for review.');
    }

    setShowSubmitConfirm(true);
  };

  const handleSubmit = async () => {
    setShowSubmitConfirm(false);

    if (!userProfile) {
      console.error('User profile is not loaded:', userProfile);
      showError('User profile not loaded. Please try logging in again.');
      navigate('/dashboard');
      return;
    }

    if (!userProfile.manager?.employeeId) {
      showError('Manager information is missing. Please contact your administrator.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Create the feedback request
      const createRequestDto = {
        employeeId: userProfile.employeeId,
        employeeName: userProfile.displayName,
        employeeEmail: userProfile.email,
        managerEmployeeId: userProfile.manager?.employeeId || '',
        managerName: userProfile.manager?.displayName || '',
        managerEmail: userProfile.manager?.email || '',
        connectName: requestName || null,
        year: new Date().getFullYear(),
        status: 'in_progress'
      };

      const response = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackRequests`,
        {
          method: 'POST',
          body: JSON.stringify(createRequestDto)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create feedback request');
      }

      const result = await response.json();
      const requestId = result.data?.id;

      if (!requestId) {
        throw new Error('No request ID returned from server');
      }

      // Submit answers
      const submitAnswersDto = {
        userId: userProfile.employeeId,
        userName: userProfile.displayName,
        answers: questions.map(q => ({
          questionId: q.id,
          answer: answers[q.id] || ''
        }))
      };

      const answersResponse = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackRequests/${requestId}/answers`,
        {
          method: 'POST',
          body: JSON.stringify(submitAnswersDto)
        }
      );

      if (!answersResponse.ok) {
        throw new Error('Failed to submit answers');
      }

      // Upload files if any are selected
      if (selectedFiles.length > 0) {
        await uploadFiles(requestId, selectedFiles);
      }

      showSuccess('Connect request submitted successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error submitting request:', error);
      showError(error instanceof Error ? error.message : 'Failed to submit connect request');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (questionsLoading || profileLoading) {
    return (
      <div className="pt-24 min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-pink-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-600 text-lg font-medium">
            {profileLoading ? 'Loading your profile...' : 'Loading connect form...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 min-h-screen bg-slate-50">
      {/* Sticky Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-40">
        <div className="h-1 bg-slate-200">
          <div
            className="h-full bg-gradient-to-r from-pink-400 to-pink-500 transition-all duration-500 ease-out"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      <main className="mx-auto px-10 lg:px-20 xl:px-32 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-pink-500 hover:text-pink-600 transition-colors mb-4 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>

          <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 rounded-2xl p-8 shadow-xl text-white">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex-1">
                {isEditingName ? (
                  <input
                    type="text"
                    value={requestName}
                    onChange={(e) => {
                      setRequestName(e.target.value);
                      if (validationErrors.name && e.target.value.trim()) {
                        setValidationErrors(prev => ({ ...prev, name: undefined }));
                      }
                    }}
                    onBlur={() => setIsEditingName(false)}
                    onKeyDown={(e) => { if (e.key === 'Enter') setIsEditingName(false); }}
                    className={`text-3xl font-bold text-white bg-transparent border-b-2 outline-none w-full pb-1 placeholder:text-white/50 ${validationErrors.name ? 'border-red-300' : 'border-white/50'}`}
                    autoFocus
                    placeholder="Enter connect name..."
                  />
                ) : (
                  <h1
                    className="text-3xl font-bold text-white flex items-center gap-2 cursor-pointer group"
                    onClick={() => setIsEditingName(true)}
                  >
                    {requestName || 'New Connect Request'}
                    <Edit3 className="w-4 h-4 text-white/60 group-hover:text-white transition-colors" />
                  </h1>
                )}
                {validationErrors.name && (
                  <p className="text-red-200 text-sm mt-1 flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Connect name is required
                  </p>
                )}
                <div className="flex items-center gap-3 mt-1.5 text-sm">
                  <p className="text-white/70">
                    Manager: <span className="font-medium text-white/90">{userProfile?.manager?.displayName || 'Not assigned'}</span>
                  </p>
                  <span className="text-white/40">·</span>
                  <p className="text-white/60">
                    {formatDateInIST(new Date())}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className={`text-xs font-semibold px-3 py-1.5 rounded-full ${
                  progressPercent === 100 
                    ? 'bg-green-400/20 text-green-100 border border-green-300/30' 
                    : 'bg-white/20 text-white border border-white/30'
                }`}>
                  {answeredCount}/{questions.length} answered
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* PIP Warning */}
        {activePip?.isOnPip && (
          <div className="mb-6 rounded-2xl p-6 border border-red-200 bg-gradient-to-br from-red-50 to-orange-50 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 p-2.5 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-medium text-slate-800 mb-1">Performance Improvement Plan (PIP)</h3>
                <p className="text-slate-600 text-sm mb-3">
                  You are currently on PIP. This connect will be marked as PIP-enabled for both you and your manager.
                </p>
                <div className="flex flex-wrap gap-3 text-sm">
                  {activePip.pipStartDate && (
                    <div className="flex items-center gap-2 text-red-700 bg-red-100 px-3 py-1.5 rounded-lg border border-red-200">
                      <Calendar className="w-4 h-4" />
                      <span className="font-semibold">Start:</span>
                      {formatDateInIST(activePip.pipStartDate)}
                    </div>
                  )}
                  {activePip.pipEndDate && (
                    <div className="flex items-center gap-2 text-red-700 bg-red-100 px-3 py-1.5 rounded-lg border border-red-200">
                      <Calendar className="w-4 h-4" />
                      <span className="font-semibold">End:</span>
                      {formatDateInIST(activePip.pipEndDate)}
                    </div>
                  )}
                </div>
                {activePip.pipReason && (
                  <div className="mt-3 p-3 bg-red-100/60 rounded-lg border border-red-200 text-red-800 text-sm">
                    <span className="font-semibold">Reason:</span>
                    <div
                      className="mt-1 rich-text-editor"
                      dangerouslySetInnerHTML={renderRichText(activePip.pipReason)}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Core Priorities - View Only */}
        {userProfile && (
          <CorePrioritiesView
            employeeId={userProfile.employeeId}
            employeeName={userProfile.displayName}
            year={new Date().getFullYear()}
          />
        )}

        {/* Questions */}
        <div className="space-y-5 mb-8">
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-gradient-to-r from-pink-200 to-transparent"></div>
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-2">
              <FileText className="w-4 h-4 text-pink-400" />
              Self-Assessment ({questions.length} Questions)
            </h2>
            <div className="h-px flex-1 bg-gradient-to-l from-pink-200 to-transparent"></div>
          </div>
          {questions.map((question, index) => (
            <div
              key={question.id}
              id={`question-${question.id}`}
              className={`rounded-2xl overflow-hidden shadow-sm transition-all ${
                validationErrors.questions?.has(question.id)
                  ? 'ring-1 ring-red-300 bg-white'
                  : 'bg-white border border-slate-200/80'
              }`}
            >
              {/* Question header */}
              <div className={`px-5 py-3.5 flex items-center gap-3 border-b ${
                validationErrors.questions?.has(question.id)
                  ? 'bg-red-50/60 border-red-100'
                  : 'bg-gradient-to-r from-slate-50 to-white border-slate-100'
              }`}>
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                  {index + 1}
                </span>
                <h3 className="text-sm font-semibold text-slate-800 leading-relaxed">
                  {question.question}
                </h3>
              </div>

              {/* Answer area */}
              <div className="p-5">
                <label className={`text-[11px] font-semibold uppercase tracking-wider mb-2 block ${
                  validationErrors.questions?.has(question.id) ? 'text-red-500' : 'text-slate-400'
                }`}>
                  Your Response {validationErrors.questions?.has(question.id) && <span className="normal-case text-red-400">— Required</span>}
                </label>
                <div className={`rounded-xl overflow-hidden border ${
                  validationErrors.questions?.has(question.id) ? 'border-red-200 bg-red-50/30' : 'border-slate-200 bg-slate-50/50'
                }`}>
                  <RichTextEditor
                    value={answers[question.id] || ''}
                    onChange={(updatedValue) => handleAnswerChange(question.id, updatedValue)}
                    placeholder={question.placeholder}
                    disabled={isSubmitting}
                    minHeightClassName="min-h-[130px]"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* File Upload Section */}
        <div className="bg-gradient-to-br from-white to-blue-50/30 rounded-2xl p-5 shadow-sm border border-blue-100 mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2.5 bg-blue-100 rounded-lg">
              <Paperclip className="w-4 h-4 text-blue-500" />
            </div>
            <div>
              <h2 className="text-base font-medium text-slate-800">
                Supporting Documents
              </h2>
              <p className="text-slate-400 text-xs">Optional — attach files to support your connect request</p>
            </div>
          </div>
          <div className="mt-4">
            <FileUpload
              onFilesChange={setSelectedFiles}
              maxFileSize={10}
              maxTotalSize={50}
              disabled={isSubmitting || isSavingDraft}
            />
          </div>
          {selectedFiles.length > 0 && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-700 text-sm font-medium">
                ✓ {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} ready to upload
              </p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="bg-gradient-to-br from-white to-slate-50 rounded-2xl p-5 shadow-sm border border-slate-200 mb-8">
          {!userProfile && (
            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-yellow-700 text-sm font-medium">
              ⚠️ User profile is loading... Please wait before submitting.
            </div>
          )}
          <div className="flex items-center justify-end gap-3">
            {/* Discard Button */}
            <button
              onClick={handleDiscard}
              disabled={isSubmitting || isSavingDraft}
              className="bg-white text-slate-600 py-2.5 px-5 rounded-xl font-medium text-sm hover:bg-slate-50 transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed border border-slate-300"
            >
              Discard
            </button>

            {/* Save as Draft Button */}
            <button
              onClick={handleSaveAsDraft}
              disabled={isSavingDraft || isSubmitting || !userProfile}
              className="bg-white text-pink-600 py-2.5 px-5 rounded-xl font-medium text-sm hover:bg-pink-50 transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed border border-pink-200"
            >
              {isSavingDraft ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Save as Draft
                </>
              )}
            </button>

            {/* Submit Button */}
            <button
              onClick={handleSubmitClick}
              disabled={isSubmitting || isSavingDraft || !userProfile}
              className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white py-2.5 px-6 rounded-xl font-medium text-sm shadow-sm shadow-pink-500/20 hover:shadow-md hover:shadow-pink-500/25 transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : !userProfile ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Loading Profile...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Submit Connect
                </>
              )}
            </button>
          </div>
        </div>
      </main>

      {/* Back to Top FAB */}
      <button
        onClick={scrollToTop}
        className={`
          fixed bottom-8 right-8 z-50 w-12 h-12 rounded-full
          bg-gradient-to-r from-pink-500 to-purple-600 text-white
          shadow-lg shadow-pink-500/30 hover:shadow-xl hover:shadow-pink-500/40
          flex items-center justify-center
          transition-all duration-300 hover:scale-110
          ${showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}
        `}
        aria-label="Back to top"
      >
        <ArrowUp className="w-5 h-5" />
      </button>

      {/* Toasts */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => hideToast(toast.id)}
        />
      ))}

      {/* Discard Confirmation Dialog */}
      {showDiscardConfirm && (
        <ConfirmDialog
          title="Discard Draft"
          message="Are you sure you want to discard this draft? This action cannot be undone and all your progress will be lost."
          confirmText="Discard Draft"
          cancelText="Continue Editing"
          type="danger"
          onConfirm={confirmDiscard}
          onCancel={() => setShowDiscardConfirm(false)}
        />
      )}

      {/* Submit Confirmation Dialog */}
      {showSubmitConfirm && (
        <ConfirmDialog
          title="Submit Connect"
          message={submitConfirmMessage}
          confirmText="Submit"
          cancelText="Cancel"
          type="info"
          onConfirm={handleSubmit}
          onCancel={() => setShowSubmitConfirm(false)}
        />
      )}
    </div>
  );
}
